// Mat-exponering — tid över säker gräns inom ett RULLANDE fönster (timmar).
//
// En kurva som bara växer över hela historiken är operativt fel: en excursion för två
// veckor sen har inget med dagens mat att göra (maten roteras). Därför mäter vi hur
// många timmar temperaturen legat över gränsen under de SENASTE N timmarna (default 24h).
// Gammal exponering åldras ut ur fönstret → kurvan stiger vid en excursion och faller
// när den passerar ut ur fönstret. Så är också livsmedelsregler formulerade
// ("max X h över Y °C per dygn"). Larm = när fönstrets tid passerar budgeten uppåt.

import { Reading } from './recoveryModel';

const HOUR_MS = 3_600_000;
const DEFAULT_GAP_CAP_MS = 6 * HOUR_MS;
const DEFAULT_WINDOW_H = 24;

export interface TimeAboveParams {
    /** säker gräns i °C — tid räknas när temp ligger över denna */
    limit: number;
    /** budget i timmar — larm när fönstrets tid-över-gräns passerar hit uppåt */
    budget: number;
    /** rullande fönster i timmar (default 24) */
    windowH?: number;
    /** cap på dt mellan avläsningar (default 6h) så en offline-lucka inte ger falsk stor summa */
    gapCapMs?: number;
}

export interface TimeAbovePoint {
    ts: number;
    /** tid över gränsen (timmar) inom det rullande fönstret som slutar här */
    hours: number;
}

export interface TimeAboveResult {
    /** rullande kurva (timmar) — stiger och faller när exponering åldras ut */
    series: TimeAbovePoint[];
    /** högsta fönstervärde under perioden (timmar) */
    peakHours: number;
    /** antal gånger fönstrets tid passerade budgeten uppåt = antal larm */
    alerts: number;
    /** tidpunkter för budget-passeringarna (för prickar i grafen) */
    crossTs: number[];
    /** fönsterlängd som användes (timmar) */
    windowH: number;
}

/**
 * Tid över `limit` inom ett rullande `windowH`-timmars fönster. Vänster-Riemann per
 * intervall, kanten prorateras. Larm räknas när fönstervärdet passerar `budget` uppåt.
 */
export function timeAboveLimit(rows: Reading[], params: TimeAboveParams): TimeAboveResult {
    const { limit, budget } = params;
    const windowH = params.windowH ?? DEFAULT_WINDOW_H;
    const gapCap = params.gapCapMs ?? DEFAULT_GAP_CAP_MS;
    if (rows.length === 0) return { series: [], peakHours: 0, alerts: 0, crossTs: [], windowH };

    const s = [...rows].sort((a, b) => a.ts - b.ts);
    const W = windowH * HOUR_MS;
    const series: TimeAbovePoint[] = [];
    const crossTs: number[] = [];
    let peak = 0, alerts = 0, prev = 0;

    for (let i = 0; i < s.length; i++) {
        const tEnd = s[i].ts;
        const wStart = tEnd - W;
        // summera tid-över-gräns för intervall som överlappar [wStart, tEnd]
        let sum = 0;
        for (let j = i - 1; j >= 0; j--) {
            const segEnd = s[j + 1].ts;
            if (segEnd <= wStart) break;           // intervallet helt före fönstret
            if (s[j].value <= limit) continue;     // inte över gränsen → 0
            const a = Math.max(s[j].ts, wStart);   // klipp mot fönstrets vänsterkant
            sum += Math.min(segEnd - a, gapCap) / HOUR_MS;
        }
        if (sum > peak) peak = sum;
        if (budget > 0 && prev < budget && sum >= budget) { alerts++; crossTs.push(tEnd); }
        prev = sum;
        series.push({ ts: tEnd, hours: sum });
    }
    return { series, peakHours: peak, alerts, crossTs, windowH };
}
